import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { ReactComponent as EmptyStateWeldPhoto } from '@bechtel/client-assets/dist/images/empty_state_weld_photo.svg';
import {
  FileUpload,
  FileUploadInput,
  FileUploadResponse,
  MilestoneStatusMessages,
  useWeldAssignees,
} from '@bechtel/client-common';
import { ADD_WELD_ATTACHMENT, GET_WELD_PHOTOS } from '@bechtel/client-gql';
import { Box, Button, FormControl, Icon, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import {
  AddWeldAttachmentError,
  AddWeldAttachmentInput,
  AddWeldAttachmentPayload,
  Attachment,
  AttachmentMetadataInput,
  CommodityAssignee,
  CommodityMilestoneActivity,
  CommodityWrapper,
  PagingInput,
  Weld,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, flatMap, get, map, uniq } from 'lodash';
import { FC, Fragment, useEffect, useState } from 'react';
import { useWeldPermissions } from '~/hooks';
import { WeldPhotoList } from './list';

interface WeldPhotosProps {
  showPhotoCount: (count: number) => void;
  weldCommodity: Weld;
  fetchMore: (e) => void;
}

export const WeldPhotos: FC<WeldPhotosProps> = ({ showPhotoCount, weldCommodity, fetchMore }) => {
  const { commodityAssignees } = weldCommodity;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(commodityAssignees);
  const { canUploadPhotos, canDeleteCommodityAttachment, canDeleteWeldAttachment } = useWeldPermissions(assignedCrewMembers);
  const activityLimit = 10;
  const { i18n } = useLingui();
  const [addAttachmentError, setAddAttachmentError] = useState<AddWeldAttachmentError | ApolloError | null>();
  const [loadWeldPhotos, { loading, error, refetch }] = useLazyQuery<
    { commoditiesByIds: CommodityWrapper[] },
    { ids: string[]; paging: PagingInput }
  >(GET_WELD_PHOTOS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const [addWeldAttachment, { loading: mutationLoading }] = useMutation<
    {
      addWeldAttachmentPayload: AddWeldAttachmentPayload;
    },
    {
      input: AddWeldAttachmentInput;
    }
  >(ADD_WELD_ATTACHMENT, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const addWeldAttachmentError = get(data, 'addWeldAttachment.error');
      if (addWeldAttachmentError) {
        setAddAttachmentError(addWeldAttachmentError);
      } else {
        loadWeldPhotos({
          variables: {
            ids: [weldCommodity.id],
            paging: {
              cursor: '1',
              limit: activityLimit,
            },
          },
        });
        if (addAttachmentError) setAddAttachmentError(null);
      }
    },
    onError: (error) => {
      setAddAttachmentError(error);
    },
  });
  const milestonesActivityHistory: CommodityMilestoneActivity[] = get(
    weldCommodity,
    'milestonesActivityHistory.items',
    []
  );
  const photosCount: number = flatMap(milestonesActivityHistory, 'attachments')?.length;
  const totalActivities: number = get(weldCommodity, 'milestonesActivityHistory.total', 0);
  const totalPages = Math.ceil(totalActivities / activityLimit);
  const cursor: number = get(weldCommodity, 'milestonesActivityHistory.cursor', 1);

  const filteredMilestonesActivityHistory = map(
    uniq(flatMap(milestonesActivityHistory, 'milestoneStatus')),
    (status) => {
      let attachmentsByStatus: Attachment[] = [];
      filter(milestonesActivityHistory, (item) => {
        if (item.milestoneStatus === status) {
          return (attachmentsByStatus = [...attachmentsByStatus, ...item.attachments]);
        }
      });
      return {
        attachments: attachmentsByStatus,
        milestoneStatus: status,
      };
    }
  );

  useEffect(() => {
    showPhotoCount(photosCount);
  }, [photosCount, weldCommodity]);

  useEffect(() => {
    if (cursor <= totalPages) {
      fetchResult();
    }
  }, [cursor, totalPages]);

  const fetchResult = async () => {
    await fetchMore({
      variables: {
        paging: {
          cursor: cursor.toString(),
          limit: activityLimit,
        },
      },
    });
  };

  return (
    <>
      {!loading && !error && !photosCount && (
        <Box
          flexDirection={{ sm: 'column' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          px={{ sm: '450' }}
          pt={{ sm: '800' }}
          pb={{ sm: '700' }}
          flexGrow={canUploadPhotos && milestonesActivityHistory?.length > 0 ? { sm: 0 } : { sm: 1 }}
        >
          <EmptyStateWeldPhoto />
          <Box my={{ sm: '500' }}>
            <Text color="grey.1000" weight="600" family="graphik" size="400">
              <Trans>Nothing to show yet</Trans>
            </Text>
          </Box>
          <Text color="grey.1000" family="graphik" size="400">
            {canUploadPhotos && milestonesActivityHistory?.length > 0
              ? t`Please upload Photo(s) to view`
              : t`Keep a check on this later…`}
          </Text>
        </Box>
      )}
      {milestonesActivityHistory?.length > 0 && canUploadPhotos && (
        <Box px={{ sm: '450' }} pt={{ sm: '550' }}>
          <FormControl label={<Trans>Upload a Photo</Trans>} css="flex-grow: 1;">
            <FileUpload
              maxFileSizeMB={10}
              acceptFileTypes="image/jpeg, image/png"
              onSuccess={async (res: FileUploadResponse, file) => {
                const newMediaData: AttachmentMetadataInput = {
                  fileName: file.name,
                  fileSize: file.size,
                  attachmentId: res.attachmentId,
                  mimeType: file.type,
                };
                await addWeldAttachment({
                  variables: {
                    input: {
                      weldId: weldCommodity.id,
                      attachmentMetadata: [newMediaData],
                    },
                  },
                });
              }}
            >
              {({ getInputProps, getRootProps, loading, acceptFileTypes }) => {
                return (
                  <Fragment>
                    <FileUploadInput
                      getInputProps={getInputProps}
                      getRootProps={getRootProps}
                      loading={loading || mutationLoading}
                      acceptFileTypes={acceptFileTypes}
                    />
                  </Fragment>
                );
              }}
            </FileUpload>
          </FormControl>
        </Box>
      )}
      {loading && (
        <Box justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} p={{ sm: '600' }} flexGrow={{ sm: 1 }}>
          <Spinner size={SpinnerSize.LARGE}></Spinner>
        </Box>
      )}
      {!loading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          px={{ sm: '450' }}
          pt={{ sm: '600' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Photos. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()} css={{ width: 'auto' }}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}

      {!loading && !error && photosCount > 0 && (
        <Box
          flexGrow={{ sm: 1 }}
          px={{ sm: '450' }}
          pt={{ sm: '500' }}
          flexDirection={{ sm: 'column' }}
          style={{ overflowY: 'auto' }}
        >
          {map(
            filteredMilestonesActivityHistory,
            (item) =>
              item.attachments &&
              item.attachments.length > 0 && (
                <>
                  <Box py={{ sm: '500' }}>
                    <Text
                      color="grey.1000"
                      weight="600"
                      family="graphik"
                      size="600"
                      transform="capitalize"
                    >{`${item?.milestoneStatus && i18n._(MilestoneStatusMessages[item?.milestoneStatus])} (${
                      item.attachments.length
                    })`}</Text>
                  </Box>
                  <WeldPhotoList
                    attachments={item?.attachments}
                    milestoneStatus={item?.milestoneStatus}
                    commodityId={weldCommodity.id}
                    canDeleteCommodityAttachment={canDeleteCommodityAttachment}
                    canDeleteWeldAttachment={canDeleteWeldAttachment}
                    onPhotoDeleted={() => {
                      loadWeldPhotos({
                        variables: {
                          ids: [weldCommodity.id],
                          paging: {
                            cursor: '1',
                            limit: activityLimit,
                          },
                        },
                      });
                    }}
                  />
                </>
              )
          )}
        </Box>
      )}
    </>
  );
};
